<template>
	<v-card v-if="currentModalFilter === 'sort'" class="sort-modal-container">
		<div
			@input="(v) => v || closeModal()"
			v-click-outside="closeModal"
			class="filter-modal-wrapper"
		>
			<v-progress-circular v-if="sortLoading" :size="20" :width="3"></v-progress-circular>
			<div
				v-else
				v-for="item in currentSortList"
				:key="item.field"
				@click="handleSortItemChange(item.field)"
				class="filter-modal__sort__input"
				:class="{ active: isInputActive('sort', item.field) }"
			>
				<label class="filter-modal__input-label">
					{{ item.title }}
				</label>
			</div>
		</div>
	</v-card>
	<v-dialog
		v-else
		v-model="filterModalIsVisible"
		max-width="600"
		@input="(v) => v || closeModal()"
		content-class="filter-modal"
		@click:outside="closeModal"
	>
		<v-card class="filter-modal__card">
			<div class="d-flex align-center flex-wrap">
				<div class="filter-modal__title">
					{{ currentModalLabel }}
					<div
						class="filter-modal__progress d-flex justify-center align-center py-6"
						v-if="isFiltersLoading"
					>
						<v-progress-circular indeterminate :size="25" :width="3" />
					</div>
				</div>
			</div>

			<div
				class="filter-modal__container mb-8 mt-3"
				ref="filterContainer"
				@scroll="handleScroll"
			>
				<div v-if="currentModalFilter === 'chapters' && chapters" class="pl-4 pb-3">
					<div
						class="filter-modal__input"
						v-for="item in chapters"
						:key="item.chapter_id"
					>
						<CustomCheckbox
							:isChecked="isInputActive('chapters', item.chapter_id)"
							:value="item.chapter_id"
							:onChange="() => setItem('chapters', item.chapter_id)"
						/>
						<label class="filter-modal__input-label">
							{{ item.chapter_name }}
						</label>
					</div>
				</div>

				<div v-if="currentModalFilter === 'tags'" class="ml-4 pb-3">
					<div
						class="filter-modal__input"
						v-for="item in computedTags"
						:key="item.tag_id"
					>
						<CustomCheckbox
							:isChecked="isInputActive('tags', item.tag_id)"
							:value="item.tag_id"
							:onChange="() => setItem('tags', item.tag_id)"
						/>
						<label class="filter-modal__input-label">
							{{ item.tag }}
						</label>
					</div>
				</div>
				<div v-if="currentModalFilter === 'author'" class="ml-4 pb-3">
					<div
						class="filter-modal__input"
						v-for="item in resourceAuthor"
						:key="item.resource_id"
					>
						<CustomCheckbox
							:isChecked="isInputActive('author', item.resource_author)"
							:value="item.resource_id"
							:onChange="() => setItem('author', item.resource_author)"
						/>
						<label class="filter-modal__input-label">
							{{ item.resource_author }}
						</label>
					</div>
				</div>

				<div v-if="currentModalFilter === 'tracks'" class="ml-4 pb-3">
					<div
						class="filter-modal__input"
						v-for="item in eventTracks"
						:key="item.track_id"
					>
						<CustomCheckbox
							:isChecked="isInputActive('tracks', item.track_id)"
							:value="item.track_id"
							:onChange="() => setItem('tracks', item.track_id)"
						/>
						<label class="filter-modal__input-label">
							{{ item.track_name }}
						</label>
					</div>
				</div>

				<div v-if="currentModalFilter === 'category'" class="pl-4 pb-3 mt-3">
					<div
						class="filter-modal__input"
						v-for="item in categoriesList"
						:key="item.category_id"
					>
						<CustomRadio
							:isChecked="isInputActive('category', item.category_id)"
							:value="item.category_id"
							:onChange="() => setItem('category', item.category_id)"
						/>
						<label class="filter-modal__input-label">
							{{ item.category_name }}
						</label>
					</div>
				</div>
				<div v-if="currentModalFilter === 'type'" class="pl-4 pb-3">
					<div class="filter-modal__input" v-for="item in resourceType" :key="item.id">
						<CustomCheckbox
							:isChecked="isInputActive('type', item.id)"
							:value="item.id"
							:onChange="() => setItem('type', item.id)"
						/>
						<label class="filter-modal__input-label">
							{{ item.name }}
						</label>
					</div>
				</div>

				<div v-if="isCustomField" class="ml-4 pb-3">
					<div
						class="filter-modal__input"
						v-for="item in currentCustomList"
						:key="item.id"
					>
						<CustomCheckbox
							:isChecked="isInputActive(currentModalFilter, item.id)"
							:value="item.id"
							:onChange="() => setItem(currentModalFilter, item.id)"
						/>
						<label class="filter-modal__input-label">
							{{ item.val }}
						</label>
					</div>
				</div>

				<div class="d-flex flex-column mb-4 mt-4" v-if="currentModalFilter === 'date'">
					<v-card-text class="px-0 py-0">
						<v-tabs
							fixed-tabs
							class="date-time-control-tabs mb-6"
							v-model="activeDateTab"
						>
							<v-tab key="fixedPeriods">
								<slot name="dateIcon">
									<span>Fixed period</span>
								</slot>
							</v-tab>

							<v-tab key="datePicker">
								<slot name="dateIcon">
									<span>Date Picker</span>
								</slot>
							</v-tab>

							<v-divider class="mb-4 d-block"></v-divider>

							<v-tab-item key="fixedPeriods">
								<v-radio-group v-model="selectedOption" class="ml-4">
									<v-radio
										key="today"
										label="Today"
										value="today"
										:color="datePickerColor"
										class="mb-4"
									></v-radio>

									<v-radio
										key="yesterday"
										label="Yesterday"
										value="yesterday"
										:color="datePickerColor"
										class="mb-4"
									></v-radio>

									<v-radio
										key="last7days"
										label="Last 7 days"
										value="last7days"
										:color="datePickerColor"
										class="mb-4"
									></v-radio>

									<v-radio
										key="last30days"
										label="Last 30 days"
										value="last30days"
										:color="datePickerColor"
										class="mb-4"
									></v-radio>

									<v-radio
										key="thisMonth"
										label="This month"
										value="thisMonth"
										:color="datePickerColor"
										class="mb-4"
									></v-radio>

									<v-radio
										key="lastMonth"
										label="Last Month"
										value="lastMonth"
										:color="datePickerColor"
										class="mb-4"
									></v-radio>
								</v-radio-group>
							</v-tab-item>

							<v-tab-item key="datePicker">
								<slot name="timeIcon">
									<v-date-time-picker
										label="Start filter date: "
										type="from"
										v-model="selectedFromTime"
										dialogWidth="500"
										okText="Apply"
										:error="dateError"
										:show-error="!!dateError"
										:datePickerProps="{ color: datePickerColor }"
										:timePickerProps="{ color: datePickerColor }"
									/>

									<v-date-time-picker
										label="End filter date: "
										type="to"
										v-model="selectedToTime"
										dialogWidth="500"
										:error="dateError"
										:show-error="!!dateError"
										:datePickerProps="{ color: datePickerColor }"
										:timePickerProps="{ color: datePickerColor }"
										okText="Apply"
									/>
								</slot>
							</v-tab-item>
						</v-tabs>
					</v-card-text>
				</div>
			</div>

			<div class="mt-4 filter-modal__action-btn-wrapper">
				<v-btn
					class="elevation-0 filter-modal__action-btn action-cancel"
					outlined
					@click="closeModal"
				>
					Cancel
				</v-btn>

				<v-btn
					class="filter-modal__action-btn action-apply"
					:loading="false"
					:disabled="false"
					@click="applySelection"
				>
					Apply
				</v-btn>
			</div>
		</v-card>
	</v-dialog>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import { startOfMonth, endOfMonth, sub } from 'date-fns'
import CustomCheckbox from '@/components/CustomCheckbox'
import CustomRadio from '@/components/CustomRadio'

export default {
	components: { CustomCheckbox, CustomRadio },
	props: {
		filterModalIsVisible: {
			type: Boolean,
			default: false,
		},
		currentModalFilter: {
			type: String,
			default: '',
		},
		currentModalLabel: {
			type: String,
			default: '',
		},
		selectedChips: {
			type: Object,
			default: () => {
			},
		},
		filters: {
			type: Object,
			default: () => {
			},
		},
		dateError: {
			type: String,
			default: '',
		},
		dateOption: {
			type: String,
			default: '',
		},
		dateRange: {
			type: Object,
			default: () => {
			},
		},
	},

	methods: {
		...mapActions('resources', [
			'getResourcesTags',
			'getResourcesTypes',
			'getResourcesAuthors',
		]),
		...mapActions('app', ['getSort']),
		...mapActions('tag', ['fetchTags', 'clearTags']),
		...mapActions('event', ['fetchEventCategories', 'clearEventCategories', 'getEventTracks']),
		...mapActions('list', ['getList', 'getSortType']),

		handleSortItemChange(field) {
			this.setItem('sort', field)
			if (['my-agenda-events', 'event-agenda'].some((item) => this.$route.name === item)) {
				this.getSortType(field)
				this.applySelection()
			} else {
				this.applySelection()
			}
		},

		isInputActive(filter, name) {
			switch (filter) {
				case 'sort': {
					if (this.filters.field) {
						return this.filters.field === name
					} else {
						const defaultItem = this.currentSortList.find((item) => item.default === 1)
						return defaultItem && defaultItem.field === name
					}
				}
				case 'category': {
					return this.filters.category_id === name
				}
				default: {
					return this.selectedChips[filter] &&
					this.selectedChips[filter].some((chip) => chip === name)
						? true
						: false
				}
			}
		},

		sortingLists() {
			let name = this.$route.name
			switch (name) {
				case 'attendees': {
					this.getSort({ name: 'users', sources: 'attendeesList' })
					break
				}
				case 'users': {
					this.getSort({ name: 'users', sources: 'nearMeList' })
					break
				}
				case 'partners': {
					this.getSort({ name: 'partners', sources: 'exhibitorsList' })
					break
				}
				case 'event-speakers': {
					this.getSort({ name: 'speakers', sources: 'speakersList' })
					break
				}
				case 'exhibitor-staff': {
					this.getSort({ name: 'users', sources: 'boothStaffList' })
					break
				}
				case 'threads': {
					this.getSort({ name: 'threads', sources: 'newsList' })
					break
				}
				case 'forums-by-category-id': {
					this.getSort({ name: 'forums', sources: 'forumsList' })
					break
				}
				case 'events': {
					this.getSort({ name: 'events', sources: 'eventsList' })
					break
				}
				case 'event-checkin': {
					this.getSort({ name: 'event-checkin', sources: 'checkInLogList' })
					break
				}
				case 'my-agenda-events': {
					this.getSort({ name: 'event-agenda', sources: 'agendaDaysList' })
					break
				}
				case 'event-agenda': {
					this.getSort({ name: 'event-agenda', sources: 'agendaList' })
					break
				}
				case 'resources': {
					this.getSort({ name: 'resources', sources: 'resourcesList' })
					break
				}
				default: {
					break
				}
			}
		},

		clearTimer() {
			clearTimeout(this.scrollTimer)
			this.scrollTimer = null
		},
		closeModal() {
			this.$emit('closeModal')
		},
		applySelection() {
			this.$emit('applySelection')
		},
		setItem(filter, item) {
			this.$emit('setItem', { filter, item })
		},
		generateAndFormatDate(date) {
			const year = date.getFullYear()
			const month = this.pad(date.getMonth() + 1)
			const day = this.pad(date.getDate())
			return year + '-' + month + '-' + day
		},
		generateAndFormatDateWithTime(value) {
			const date = new Date(value)
			const year = date.getFullYear()
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			const hours = String(date.getHours()).padStart(2, '0')
			const minutes = String(date.getMinutes()).padStart(2, '0')

			return `${year}-${month}-${day} ${hours}:${minutes}`
		},
		pad(number) {
			return (number < 10 ? '0' : '') + number
		},
		handleScroll() {
			if (!this.scrollTimer) {
				const container = this.$refs.filterContainer
				if (container.scrollTop + container.clientHeight >= container.scrollHeight - 5) {
					if (!this.isFiltersLoading && !this.isLastPage) {
						const increasePage = (num) => {
							const page = num + 1
							this.page = page
							return page
						}
						this.scrollTimer = setTimeout(() => {
							switch (this.currentModalFilter) {
								case 'author': {
									this.getResourcesAuthors({ page: increasePage(this.page) })
									break
								}
								case 'type': {
									this.getResourcesTypes({ page: increasePage(this.page) })
									break
								}
								case 'tags': {
									if (
										['resources', 'resource'].some(
											(item) => this.$route.name === item,
										)
									) {
										this.getResourcesTags({ page: increasePage(this.page) })
									} else {
										this.fetchTags(increasePage(this.page))
									}
									break
								}
								case 'category': {
									this.fetchEventCategories({ page: increasePage(this.page) })
									break
								}
								case 'tracks': {
									this.getEventTracks({
										eventId: this.$route.params.id,
										page: increasePage(this.page),
									})
									break
								}
								default: {
									if (
										this.isCustomField &&
										this.listsMeta.some(
											(list) => list.source === this.currentModalFilter,
										)
									) {
										this.getList({
											name: this.currentModalFilter,
											query: { page: increasePage(this.page) },
										})
									}
								}
							}
						}, 500)
					}
				}
			}
		},
	},
	computed: {
		...mapGetters('auth', ['profile']),
		...mapGetters('tag', ['tags', 'isTagsLoading', 'tagsMeta']),
		...mapGetters('event', [
			'categories',
			'isCategoriesLoading',
			'isTracksLoading',
			'categoriesMeta',
			'eventTracksMeta',
			'eventTracks',
		]),
		...mapGetters('app', ['sortLists', 'sortLoading']),
		...mapGetters('list', ['listsMeta', 'lists', 'isListsLoading', 'listsPageMeta']),
		...mapGetters('resources', [
			'resourceTags',
			'resourceTagMeta',
			'resourceTypeMeta',
			'resourceType',
			'resourceAuthor',
			'resourceAuthorMeta',
		]),
		currentSortList() {
			if (!this.sortLists) {
				return []
			}
			return this.sortLists
		},

		computedTags() {
			return ['resources', 'resource'].some((item) => this.$route.name === item)
				? this.resourceTags
				: this.tags
		},

		chapters() {
			return this.profile && this.profile.chapters.filter((item) => !item.hidden_subgroup)
		},
		categoriesList() {
			return this.categories.map(({ category_id, category_name }) => {
				return { category_id, category_name }
			})
		},
		datePickerColor() {
			const { navigation_bar_tint: navigationBarTint } = this.styles || {}
			return navigationBarTint + ' !important'
		},
		isCustomField() {
			return this.defaultFilters.every((filter) => filter !== this.currentModalFilter)
		},
		currentCustomList() {
			if (
				this.defaultFilters.every((filter) => filter !== this.currentModalFilter) &&
				this.listsMeta.some((list) => list.source === this.currentModalFilter)
			) {
				return this.lists && this.lists[this.currentModalFilter]
					? this.lists[this.currentModalFilter]
					: []
			}
			return []
		},
		isFiltersLoading() {
			return (
				this.isCategoriesLoading ||
				this.isTagsLoading ||
				this.isListsLoading ||
				this.isTracksLoading
			)
		},
		isLastPage() {
			switch (this.currentModalFilter) {
				case 'author': {
					return this.resourceAuthorMeta && this.resourceAuthorMeta.pageCount <= this.page
				}
				case 'type': {
					return this.resourceTypeMeta && this.resourceTypeMeta.pageCount <= this.page
				}
				case 'tags': {
					if (['resources', 'resource'].some((item) => this.$route.name === item)) {
						return this.resourceTagMeta && this.resourceTagMeta.pageCount <= this.page
					} else {
						return this.tagsMeta && this.tagsMeta.pageCount <= this.page
					}
				}
				case 'category': {
					return this.categoriesMeta && this.categoriesMeta.pageCount <= this.page
				}
				case 'tracks': {
					return this.eventTracksMeta && this.eventTracksMeta.pageCount <= this.page
				}
				default: {
					if (!this.isCustomField || !this.listsPageMeta[this.currentModalFilter]) {
						return true
					} else {
						return +this.listsPageMeta[this.currentModalFilter].pageCount <= this.page
					}
				}
			}
		},
	},
	watch: {
		selectedOption(option) {
			let fromDate = ''
			let fromTime = ''
			let toDate = ''
			let toTime = ''

			const currentDate = new Date()

			switch (option) {
				case 'today':
					fromDate = this.generateAndFormatDate(currentDate)
					toDate = this.generateAndFormatDate(currentDate)

					break
				case 'yesterday':
					const yesterday = sub(currentDate, { days: 1 })

					fromDate = this.generateAndFormatDate(yesterday)
					fromTime = '00:00'

					toDate = this.generateAndFormatDate(yesterday)
					toTime = '23:59'

					break
				case 'last7days':
					const last7DaysFrom = sub(currentDate, { days: 6 })

					fromDate = this.generateAndFormatDate(last7DaysFrom)
					toDate = this.generateAndFormatDate(currentDate)
					break
				case 'last30days':
					const last30DaysTo = currentDate
					const last30DaysFrom = sub(last30DaysTo, { days: 29 })

					fromDate = this.generateAndFormatDate(last30DaysFrom)
					toDate = this.generateAndFormatDate(last30DaysTo)

					break
				case 'thisMonth':
					const startThisMonth = startOfMonth(currentDate)
					const endThisMonth = endOfMonth(currentDate)

					fromDate = this.generateAndFormatDate(startThisMonth)
					toDate = this.generateAndFormatDate(endThisMonth)

					break
				case 'lastMonth':
					const endDate = sub(currentDate, { months: 1 })
					const startLastMonth = startOfMonth(endDate)
					const endLastMonth = endOfMonth(endDate)

					fromDate = this.generateAndFormatDate(startLastMonth)
					toDate = this.generateAndFormatDate(endLastMonth)

					break
				default:
					fromDate = this.generateAndFormatDate(new Date())
					toDate = this.generateAndFormatDate(new Date())
			}
			this.setItem('date', {
				date: { to: `${toDate} 23:59`, from: `${fromDate} 00:00` },
				option,
			})
		},
		selectedFromTime(newValue) {
			this.setItem('date', {
				date: { from: newValue ? this.generateAndFormatDateWithTime(newValue) : null },
				option: newValue ? null : 'clear',
			})
		},
		selectedToTime(newValue) {
			this.setItem('date', {
				date: { to: newValue ? this.generateAndFormatDateWithTime(newValue) : null },
				option: newValue ? null : 'clear',
			})
		},
		tags() {
			this.clearTimer()
		},
		resourceTags() {
			this.clearTimer()
		},
		categories() {
			this.clearTimer()
		},
		isListsLoading(newValue) {
			!newValue && this.clearTimer()
		},
	},
	created() {
		if (this.currentModalFilter === 'sort') {
			this.sortingLists()
		}
		if (this.currentModalFilter === 'author') {
			const page = this.resourceAuthor.length === 0 ? 1 : +this.resourceAuthorMeta.currentPage
			this.page = page
			!this.resourceAuthor.length && this.getResourcesAuthors({ page })
		}
		if (this.currentModalFilter === 'type') {
			const page = this.resourceType.length === 0 ? 1 : +this.resourceTypeMeta.currentPage
			this.page = page
			!this.resourceType.length && this.getResourcesTypes({ page })
		}
		if (this.currentModalFilter === 'tags') {
			if (['resources', 'resource'].some((item) => this.$route.name === item)) {
				const page = this.resourceTags.length === 0 ? 1 : +this.resourceTagMeta.currentPage
				this.page = page
				!this.resourceTags.length && this.getResourcesTags({ page })
			} else {
				const page = this.tags.length === 0 ? 1 : +this.tagsMeta.currentPage
				this.page = page
				!this.tags.length && this.fetchTags(page)
			}

		} else if (this.currentModalFilter === 'category') {
			const page = this.categories.length === 0 ? 1 : +this.categoriesMeta.currentPage
			this.page = page
			!this.categories.length && this.fetchEventCategories({ page })
		} else if (
			this.isCustomField &&
			this.listsMeta.some((list) => list.source === this.currentModalFilter)
		) {
			const meta = this.listsPageMeta[this.currentModalFilter]
			const page = meta && meta.currentPage ? +meta.currentPage : 1
			this.page = page
			if (!this.lists[this.currentModalFilter]) {
				this.getList({ name: this.currentModalFilter, page })
			}
		}
	},
	data() {
		return {
			hasMoreItems: true,
			page: 1,
			selectedFromTime: null,
			selectedToTime: null,
			scrollTimer: null,
			activeDateTab: 'fixedPeriods',
			selectedOption: this.dateOption,
			defaultFilters: [
				'clear',
				'chapters',
				'tags',
				'category',
				'date',
				'past',
				'myposts',
				'admin',
				'users',
				'register',
				'schedule',
				'categories',
				'follow',
				'sort',
			],
		}
	},
}
</script>
<style lang="scss">
.sort-modal-container {
	display: flex;
	justify-content: flex-end;
	@media (max-width: 480px) {
		justify-content: center;
	}
}

.filter-modal-wrapper {
	position: absolute;
	margin-top: 20px;
	align-items: flex-end !important;
	z-index: 1000;
	background: white;
	border-radius: 10px;
	border: 1.5px solid #e2e2e2;
	width: 250px;
	max-height: fit-content;
	padding: 16px;
}

.filter-modal__sort__input {
	max-width: 100%;
	display: flex;
	padding: 12px;
	margin-top: 2px;
}

.filter-modal__sort__input.active {
	background-color: #f3f3f6 !important;
	color: #fff !important;
	border-radius: 8px;
}

.filter-modal__sort__input:hover {
	background-color: #f3f3f6;
	border-radius: 8px;
}

.filter-modal__input-label {
	font-size: 16px;
	margin-left: 8px;
	color: #4a4a4a;
	font-family: Montserrat, sans-serif;
}

.filter-modal {
	border-radius: 13px;
	max-height: 800px !important;
	overflow-y: hidden;

	&__container {
		max-height: 500px;
		overflow: scroll;

		@media (max-height: 750px) {
			max-height: 50vh;
		}
	}

	&__card {
		padding: 35px;
		position: static;
	}

	&__input {
		margin: 16px 0;
		display: flex;
		align-items: center;

		&-label {
			font-size: 16px;
			font-family: Montserrat, sans-serif;
			color: #4a4a4a;
			position: relative;
			top: 1px;
		}
	}

	&__progress {
		position: absolute;
		left: calc(100% + 12px);
		top: 50%;
		transform: translateY(-50%);
	}

	&__title {
		position: relative;
		color: rgb(74, 74, 74);
		font-size: 24px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		margin-bottom: 10px;
		text-transform: capitalize;
	}

	&__action-btn {
		border-radius: 8px !important;
		text-transform: capitalize;
		font-size: 16px;
		font-weight: 700;
		box-shadow: none !important;
		padding: 0 32px !important;

		@media (max-width: 480px) {
			flex-grow: 1;
			padding: 0 !important;

			&-wrapper {
				display: flex;
				flex-wrap: nowrap;
			}
		}
	}

	.action-cancel {
		background-color: #fff !important;
		color: var(--accent-color) !important;
		border: 2px solid var(--accent-color);
		margin-right: 8px;
	}

	.action-apply {
		margin-left: 8px;
	}
}
</style>
